<script setup>
import {ref, computed, watch} from 'vue';
import NoMembershipChip from '~/components/memberships/NoMembershipChip.vue';
import {membershipWarningsText} from '~/utils/entryRequirements';
import UserProfilePic from "@/components/users/UserProfilePic.vue";
import TlDialog from "@/components/TlDialog.vue";

// Admin "join request" row (mockup requests.html .chal-row): face + applicant
// name + what they want to join, and a "Review" action that opens the
// approve/reject dialog (per-area access toggles + division picker live there).
const props = defineProps({
  request: {
    type: Object,
    required: true
  }
});

const requestDialog = ref(false);
const rejectConfirm = ref(false);
// R22 Low 3: the "Message" chip on the row is a button that shows the applicant's message under the row (it looked
// clickable and did nothing). Only drawn when there is a message.
const messageOpen = ref(false);
// Each ref is the "don't grant access" checkbox for that area (inverted on send).
const ladderAccess = ref(false);
const leagueCycleAccess = ref(false);
const tournamentAccess = ref(false);
const americanoCycleAccess = ref(false);
const meetAccess = ref(false);
const leagueCycleDivisionId = ref(null);

const errors = ref({});
const {notify} = useListenerService();
const {setAlertMessage} = useAlertService();
const {
  item: approvedItem,
  deleteItem,
  updateItem,
  formPending: loading,
  formErrors: deleteError,
  hasFormErrors
} = useCrudApi('api/v2/accessApplications');

// "Wants to join …" — the ladder/cycle/tournament(s) requested, else the club itself.
const targetText = computed(() => {
  const names = [];
  if (props.request.ladder) names.push(props.request.ladder.name);
  if (props.request.leagueCycle) names.push(props.request.leagueCycle.name);
  if (props.request.tournament) names.push(props.request.tournament.name);
  if (props.request.americanoCycle) names.push(americanoCycleLabel.value);
  if (props.request.meet) names.push(props.request.meet.name);
  if (names.length) return `Wants to join ${names.join(' · ')}`;
  return `Wants to join ${props.request.club.name}`;
});

// "Americano league · Cycle" — the application references the cycle (like leagues)
const americanoCycleLabel = computed(() => {
  const cycle = props.request.americanoCycle;
  if (!cycle) return '';
  return [cycle.americanoName, cycle.name].filter(Boolean).join(' · ');
});

// Already a club member asking into a competition: no club access to grant
// (read defensively — older APIs may not send memberOf on the applicant).
const alreadyMember = computed(() => {
  const memberOf = props.request.user?.memberOf;
  const clubId = String(props.request.clubId ?? props.request.club?.id ?? '').toLowerCase();
  return Array.isArray(memberOf) && !!clubId && memberOf.some((id) => String(id).toLowerCase() === clubId);
});
const hasCompetition = computed(() => !!(props.request.ladder || props.request.leagueCycle || props.request.tournament ||
    props.request.americanoCycle || props.request.meet));

// "Approve club, ladder and tournament access" — composed from the areas being granted.
const buttonText = computed(() => {
  const granted = alreadyMember.value ? [] : ['club'];
  if (props.request.ladder && !ladderAccess.value) granted.push('ladder');
  if (props.request.leagueCycle && !leagueCycleAccess.value) granted.push('box league');
  if (props.request.tournament && !tournamentAccess.value) granted.push('tournament');
  if (props.request.americanoCycle && !americanoCycleAccess.value) granted.push('Americano league');
  if (props.request.meet && !meetAccess.value) granted.push('meet');
  if (granted.length === 0) {
    return 'Approve';
  }
  if (granted.length === 1) {
    if (alreadyMember.value) return `Approve ${granted[0]} access`;
    return hasCompetition.value
        ? 'Approve only club access'
        : 'Approve club access';
  }
  const last = granted.pop();
  return `Approve ${granted.join(', ')} and ${last} access`;
});

// Fee awareness: the API charges the applicant automatically on approval (they
// saved a card when applying). This just tells the admin it will happen.
const {money} = usePaymentsDisplay();
const entryFeeNote = computed(() => {
  const parts = [];
  if (props.request.ladder?.entryFeeMinor && !ladderAccess.value)
    parts.push(`${money(props.request.ladder.entryFeeMinor, props.request.ladder.entryFeeCurrency)} for ${props.request.ladder.name}`);
  if (props.request.leagueCycle?.entryFeeMinor && !leagueCycleAccess.value)
    parts.push(`${money(props.request.leagueCycle.entryFeeMinor, props.request.leagueCycle.entryFeeCurrency)} for ${props.request.leagueCycle.name}`);
  if (props.request.tournament?.entryFeeMinor && !tournamentAccess.value)
    parts.push(`${money(props.request.tournament.entryFeeMinor, props.request.tournament.entryFeeCurrency)} for ${props.request.tournament.name}`);
  if (props.request.americanoCycle?.entryFeeMinor && !americanoCycleAccess.value)
    parts.push(`${money(props.request.americanoCycle.entryFeeMinor, props.request.americanoCycle.entryFeeCurrency)} for ${americanoCycleLabel.value}`);
  if (!parts.length) return null;
  return `On approval, ${props.request.user.fullName} will owe ${parts.join(' and ')}. They pay it themselves, or you can waive it from that competition's Payments page.`;
});

const cancelRequest = async (id) => {
  // deleteItem() returns nothing and surfaces failures via hasFormErrors — mirror
  // approveRequest(). (The old `res.data` threw because res is undefined, which left
  // the dialog open even though the reject had gone through.)
  rejectConfirm.value = false;
  await deleteItem(id);
  if (!hasFormErrors.value) {
    notify('requestsUpdated', props.request);
    setAlertMessage(`${props.request.user.fullName}'s request declined. They've been emailed`);
    requestDialog.value = false;
  } else {
    errors.value = deleteError.value;
    setAlertMessage({message: deleteError.value?.global?.[0] || 'Could not decline the request', color: 'error'});
  }
}

const approveRequest = async () => {
  let data = {
    ladderAccess: !ladderAccess.value,
    leagueCycleAccess: !leagueCycleAccess.value,
    tournamentAccess: !tournamentAccess.value,
    americanoCycleAccess: !americanoCycleAccess.value,
    meetAccess: !meetAccess.value,
    leagueCycleDivisionId: leagueCycleDivisionId.value,
  };
  // updateItem() returns nothing — the saved response lands on item.
  await updateItem(props.request.id, data);
  if (!hasFormErrors.value) {
    const res = approvedItem.value;
    notify('requestsUpdated', res);
    // D15: approving never refuses; a missing membership is said.
    const warned = membershipWarningsText(res?.membershipWarnings);
    setAlertMessage(warned
        ? {message: `${props.request.user.fullName} approved. ${warned}`, color: 'warning'}
        : res?.movedToWaitingArea
            ? `${props.request.user.fullName} approved. They're in the waiting area for you to place`
            : `${props.request.user.fullName} approved`);
    requestDialog.value = false;
  } else {
    errors.value = deleteError.value;
  }
}

watch(requestDialog, () => {
  leagueCycleDivisionId.value = null;
  ladderAccess.value = false;
  leagueCycleAccess.value = false;
  tournamentAccess.value = false;
  americanoCycleAccess.value = false;
  meetAccess.value = false;
})
</script>

<template>
  <div class="aac-row">
    <user-profile-pic :user="request.user" size="small" class="aac-face"/>

    <div class="aac-who">
      <div class="aac-title">{{ request.user.fullName }}</div>
      <div class="aac-sub">{{ targetText }}</div>
      <button v-if="request.message" type="button" class="tl-chip tl-chip--brand aac-msg aac-msg-btn"
              :aria-expanded="messageOpen" @click="messageOpen = !messageOpen">
        <v-icon icon="mdi-message-text" size="11" class="mr-1"/>
        Message
        <v-icon :icon="messageOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="12" class="ml-1"/>
      </button>
      <no-membership-chip v-if="request.noCurrentMembership" size="x-small" class="aac-msg"/>
      <span v-if="request.waitingForMembershipApplicationId" class="tl-chip aac-msg">
        <v-icon icon="mdi-card-account-details-outline" size="11" class="mr-1"/>Waiting for their membership
      </span>
      <div v-if="request.message && messageOpen" class="aac-message">{{ request.message }}</div>
    </div>

    <v-btn variant="outlined" size="small" @click="requestDialog = true">Review</v-btn>
  </div>

  <tl-dialog v-model="requestDialog" title="Approve access" :max-width="500">
    <div class="aac-note">
      <template v-if="alreadyMember">
        {{ request.user.fullName }} is already a player at {{ request.club.name }}.
      </template>
      <template v-else>
        {{ request.user.fullName }} is requesting access to {{ request.club.name }}. Accepting this will make them a
        player at the club<template v-if="!request.club.hideUserDetails"> and able to view other players' contact details</template>.
      </template>
    </div>

    <div v-if="request.message" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-message-text</v-icon>
      <div>
        <strong>{{ request.user.fullName }} left the following message:</strong>
        <div>{{ request.message }}</div>
      </div>
    </div>

    <div v-if="request.ladder" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-ladder</v-icon>
      <div>
        {{ request.user.fullName }} will also gain access to the ladder <strong>{{ request.ladder.name }}</strong>.
        <v-checkbox :hide-details="true" v-model="ladderAccess" label="Don't grant access to this ladder"/>
        <div v-if="!ladderAccess && request.ladder.holdsJoinsForPlacement" class="text-caption text-medium-emphasis">
          {{ (request.ladder.playersPerTeam ?? 1) > 1
              ? "They'll join the ladder's waiting area for you to pair and place."
              : "They'll join the ladder's waiting area for you to place." }}
        </div>
      </div>
    </div>

    <div v-if="request.leagueCycle" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-table</v-icon>
      <div>
        {{ request.user.fullName }} will also gain access to the box league cycle:
        <strong>{{ request.leagueCycle.name }}</strong>.
        <v-checkbox :hide-details="true" v-model="leagueCycleAccess" label="Don't grant access to this cycle"/>
        <!-- Singles cycle: a division can be chosen now (optional). Doubles: the waiting area (JOIN_SETTINGS_PLAN.md D10). -->
        <crud-list-loader
            v-if="!leagueCycleAccess && (request.leagueCycle.playersPerTeam ?? 1) === 1"
            path="api/v2/leagueCycleDivisions"
            :per-page="1000"
            :custom-filters="{ leagueCycleIds: [request.leagueCycle.id] }"
        >
          <template #default="{ items }">
            <div class="tl-label mb-2">Division <span class="text-medium-emphasis font-weight-regular">(or leave blank to place them later)</span></div>
            <v-select :items="items" item-title="name" item-value="id" v-model="leagueCycleDivisionId" clearable hide-details density="compact"/>
          </template>
        </crud-list-loader>
        <div v-else-if="!leagueCycleAccess" class="text-caption text-medium-emphasis">
          They'll join the cycle's waiting area for you to pair and place.
        </div>
      </div>
    </div>

    <div v-if="request.tournament" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-trophy</v-icon>
      <div>
        {{ request.user.fullName }} will also gain access to the tournament
        <strong>{{ request.tournament.name }}</strong>.
        <v-checkbox :hide-details="true" v-model="tournamentAccess" label="Don't grant access to this tournament"/>
      </div>
    </div>

    <div v-if="request.americanoCycle" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-account-switch</v-icon>
      <div>
        {{ request.user.fullName }} will also gain access to the Americano league cycle:
        <strong>{{ americanoCycleLabel }}</strong>.
        <v-checkbox :hide-details="true" v-model="americanoCycleAccess" label="Don't grant access to this cycle"/>
        <div v-if="!americanoCycleAccess" class="text-caption text-medium-emphasis">
          They'll join the cycle's waiting area. Give them a seat from the cycle page, or include them next cycle.
        </div>
      </div>
    </div>

    <div v-if="request.meet" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="success">mdi-handshake</v-icon>
      <div>
        {{ request.user.fullName }} will also join the meet:
        <strong>{{ request.meet.name }}</strong>.
        <v-checkbox :hide-details="true" v-model="meetAccess" label="Don't add them to this meet"/>
      </div>
    </div>

    <div v-if="entryFeeNote" class="aac-note d-flex mt-3">
      <v-icon class="mr-2" color="primary">mdi-credit-card-outline</v-icon>
      <div>{{ entryFeeNote }}</div>
    </div>

    <crud-error-display class="mt-3" :errors="errors"/>

    <template #actions>
      <v-btn variant="outlined" color="error" :loading="loading" @click="rejectConfirm = true">Reject</v-btn>
      <v-btn color="primary" :loading="loading" @click="approveRequest">{{ buttonText }}</v-btn>
    </template>
  </tl-dialog>

  <tl-dialog v-model="rejectConfirm" :title="`Reject ${request.user.fullName}'s request?`" :max-width="400">
    <p class="aac-confirm">
      They'll get an email saying it was declined.
    </p>
    <template #actions>
      <v-btn variant="outlined" @click="rejectConfirm = false">Cancel</v-btn>
      <v-btn color="error" variant="flat" :loading="loading" @click="cancelRequest(request.id)">Reject request</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
/* join-request row (mockup .chal-row) */
.aac-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;

  /* divider when several rows are stacked in one card */
  &:not(:first-child) {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.aac-face {
  flex-shrink: 0;
}

.aac-who {
  flex: 1;
  min-width: 0;
}

.aac-title {
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aac-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

.aac-msg {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  padding: 2px 8px;
  margin-top: 6px;
}

.aac-msg-btn {
  cursor: pointer;
  font-family: inherit;
  border: 0;
}

/* the applicant's message, opened from the chip */
.aac-message {
  margin-top: 6px;
  padding: 8px 10px;
  font-size: 12.5px;
  line-height: 1.5;
  white-space: pre-line;
  color: rgb(var(--v-theme-text));
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 8px;
}

.aac-confirm {
  font-size: 13.5px;
  line-height: 1.6;
  margin: 0;
}

/* light info box inside the review dialog */
.aac-note {
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  padding: 12px;
  font-size: 13px;
  line-height: 1.55;
}
</style>
